/**
 * SdpAlphabetPanel — the seven-letter alphabet of structural duality
 * principles that index the grade monoid G = ⊕_s ℤ/2[Φ(s)].
 *
 * Mirrors chapter 3 §3.0 / §3.2 of the manuscript: every confused-
 * deputy attack on an LLM agent, MCP server, RAG pipeline, or
 * autonomous deploy loop smuggles one of these seven SDP-adjunction
 * units or counits past a check that did not type the seam by SDP.
 * Naming each letter lets the type-checker reject the attack.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo, useState } from "react";
import { SDPS, type SdpCatalogEntry } from "../data/sdps.js";
import { PanelSurface, panelTokens } from "@csk/demo-ui";

const FILTER_ALL = "all" as const;

export function SdpAlphabetPanel(): React.JSX.Element {
  const [filter, setFilter] = useState<string>(FILTER_ALL);

  const filterItems = useMemo(
    () => [
      { id: FILTER_ALL, label: "All seven", color: panelTokens.accentCyan },
      ...SDPS.map((sdp) => ({
        id: sdp.code,
        label: `${sdp.code} · ${sdp.name}`,
        color: sdp.color,
      })),
    ],
    [],
  );

  const visible = useMemo(
    () =>
      filter === FILTER_ALL ? SDPS : SDPS.filter((s) => s.code === filter),
    [filter],
  );

  return (
    <PanelSurface.PanelShell
      title="Seven SDPs"
      subtitle="THE TYPING-KIT ALPHABET · SEVEN LETTERS THAT INDEX THE GRADE MONOID"
      accentColor="#ec4899"
    >
      <PanelSurface.Card glowColor="#ec489944">
        <PanelSurface.SectionHeading>An alphabet, not a checklist</PanelSurface.SectionHeading>
        <p className="mt-2.5 text-[14px] leading-[1.65]">
          Think of the seven Structural Duality Principles as the{" "}
          <em>letters</em> in which every confused-deputy attack is written. An
          LLM agent smuggles a delegation across the wrong scope (
          <PanelSurface.InlineCode>Dt</PanelSurface.InlineCode>); an MCP server presents a read capability
          at a write seam (<PanelSurface.InlineCode>Pa</PanelSurface.InlineCode>); a RAG pipeline launders
          provenance (<PanelSurface.InlineCode>Tr</PanelSurface.InlineCode>); an autonomous pipeline
          replays a consumed quota (<PanelSurface.InlineCode>St</PanelSurface.InlineCode>) or fabricates a
          precommit (<PanelSurface.InlineCode>At</PanelSurface.InlineCode>). Each attack is one letter
          presented at a seam that does not type by SDP. Giving the seam the
          letter is what makes the attack structurally inexpressible.
        </p>
        <p className="mt-2.5 text-[13.5px] leading-[1.65] text-[var(--tcc-muted)]">
          Each SDP comes as an adjunction <strong>F ⊣ G</strong>. The unit{" "}
          <PanelSurface.InlineCode>η_s</PanelSurface.InlineCode> is the grant phase (license-to-cross);
          the counit <PanelSurface.InlineCode>ε_s</PanelSurface.InlineCode>
          is the discharge phase (license-to-receive). Both phases live
          independently in the grade vector — and adding the same component
          twice cancels back to zero in ℤ/2. That single algebraic fact is what
          makes replay structurally unconstructable.
        </p>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Filter</PanelSurface.SectionHeading>
        <div className="mt-2.5">
          <PanelSurface.ChipRow
            items={filterItems}
            activeId={filter}
            onChange={setFilter}
            ariaLabel="Filter SDPs"
          />
        </div>
      </PanelSurface.Card>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(420px,1fr))] gap-[18px]">
        {visible.map((sdp) => (
          <SdpCard key={sdp.code} sdp={sdp} />
        ))}
      </div>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>How these compose into bridges</PanelSurface.SectionHeading>
        <p className="mt-2.5 text-[13.5px] leading-[1.65]">
          Each letter on this page becomes a one-hot component of the grade
          vector — fourteen components total, two phases per SDP. The{" "}
          <strong>Grade Monoid</strong> panel shows how these components combine
          under ℤ/2 addition (self-cancelling, ICG envelopes on the seven
          diagonals). The <strong>Bridge Constructor</strong> panel then lets
          you build a bridge from those components and watch the type-checker
          decide whether it can cross a seam.
        </p>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}

interface SdpCardProps {
  readonly sdp: SdpCatalogEntry;
}

function SdpCard({ sdp }: SdpCardProps): React.JSX.Element {
  return (
    <PanelSurface.Card glowColor={`${sdp.color}40`}>
      <div className="grid grid-cols-[76px_minmax(0,1fr)] items-start gap-4">
        <div
          aria-hidden
          className="inline-flex h-[76px] w-[76px] items-center justify-center rounded-2xl border border-solid font-mono text-[30px] font-extrabold tracking-[0.04em]"
          style={{
            borderColor: `${sdp.color}66`,
            background: `linear-gradient(135deg, ${sdp.color}24, transparent 80%)`,
            color: sdp.color,
            textShadow: `0 0 24px ${sdp.color}99`,
          }}
        >
          {sdp.code}
        </div>
        <div className="min-w-0">
          <div className="text-[19px] font-extrabold leading-[1.15] tracking-[-0.01em] text-[var(--tcc-bright)]">
            {sdp.name}
          </div>
          <div className="mt-1 font-mono text-[12.5px] text-[var(--tcc-muted)]">
            {sdp.adjunction}
          </div>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {PanelSurface.badge(sdp.neutralizationTheorems.join(" + "), sdp.color)}
            {PanelSurface.badge(sdp.icg, panelTokens.accentPurple)}
            {sdp.dualPairGuarantee
              ? PanelSurface.badge(sdp.dualPairGuarantee, panelTokens.accentTeal)
              : null}
          </div>
        </div>
      </div>

      <div
        className="mt-3.5 font-mono text-[11.5px] uppercase tracking-[0.06em]"
        style={{ color: sdp.color }}
      >
        AI scenario · {sdp.aiScenario}
      </div>
      <p className="mt-1.5 text-[13px] leading-[1.6] text-[var(--tcc-text)]">
        {sdp.aiSummary}
      </p>

      <div className="mt-3.5 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-2.5">
        <PhaseBox
          color={sdp.color}
          label={`unit (η_${sdp.code}) · grant phase`}
          description={sdp.unitDescription}
        />
        <PhaseBox
          color={sdp.color}
          label={`counit (ε_${sdp.code}) · discharge phase`}
          description={sdp.counitDescription}
        />
      </div>

      <div className="mt-3.5 border-t border-dashed border-[var(--tcc-border)] pt-3 text-[12px] leading-[1.55] text-[var(--tcc-muted)]">
        <span
          className="mr-1.5 font-mono font-bold tracking-[0.05em]"
          style={{ color: panelTokens.accentRed }}
        >
          Refuses:
        </span>
        {sdp.concreteAttack}
      </div>
    </PanelSurface.Card>
  );
}

interface PhaseBoxProps {
  readonly color: string;
  readonly label: string;
  readonly description: string;
}

function PhaseBox({
  color,
  label,
  description,
}: PhaseBoxProps): React.JSX.Element {
  return (
    <div
      className="rounded-[10px] border border-solid px-3 py-2.5"
      style={{
        borderColor: `${color}44`,
        background: `linear-gradient(180deg, ${color}10, rgba(5,8,16,0.55))`,
      }}
    >
      <div
        className="mb-1 font-mono text-[11px] font-bold tracking-[0.04em]"
        style={{ color }}
      >
        {label}
      </div>
      <div className="text-[12.5px] leading-[1.5] text-[var(--tcc-text)]">
        {description}
      </div>
    </div>
  );
}
